import React, { useState } from 'react'
import { 
  BookOpen, Code, Palette, TrendingUp, Users, Calculator, Camera, Heart, Globe,
  Monitor, Smartphone, Database, Cloud, Cpu, Settings, Briefcase, GraduationCap,
  Music, Video, Gamepad2, Wrench, Lightbulb, Star, Award, Target,
  BarChart3, PieChart, LineChart, Activity, Zap, Shield, Lock, Key
} from 'lucide-react'

// Lista de ícones disponíveis para categorias
const availableIcons = [
  { name: 'BookOpen', icon: BookOpen, label: 'Livro/Educação' },
  { name: 'Code', icon: Code, label: 'Programação' },
  { name: 'Palette', icon: Palette, label: 'Design/Arte' },
  { name: 'TrendingUp', icon: TrendingUp, label: 'Marketing/Vendas' },
  { name: 'Users', icon: Users, label: 'Gestão/RH' },
  { name: 'Calculator', icon: Calculator, label: 'Finanças/Contabilidade' },
  { name: 'Camera', icon: Camera, label: 'Fotografia/Mídia' },
  { name: 'Heart', icon: Heart, label: 'Saúde/Bem-estar' },
  { name: 'Globe', icon: Globe, label: 'Idiomas/Internacional' },
  { name: 'Monitor', icon: Monitor, label: 'Tecnologia' },
  { name: 'Smartphone', icon: Smartphone, label: 'Mobile/Apps' },
  { name: 'Database', icon: Database, label: 'Dados/Analytics' },
  { name: 'Cloud', icon: Cloud, label: 'Cloud/Infraestrutura' },
  { name: 'Cpu', icon: Cpu, label: 'Hardware/Sistemas' },
  { name: 'Settings', icon: Settings, label: 'Configuração/Admin' },
  { name: 'Briefcase', icon: Briefcase, label: 'Negócios' },
  { name: 'GraduationCap', icon: GraduationCap, label: 'Educação Superior' },
  { name: 'Music', icon: Music, label: 'Música/Áudio' },
  { name: 'Video', icon: Video, label: 'Vídeo/Cinema' },
  { name: 'Gamepad2', icon: Gamepad2, label: 'Games/Entretenimento' },
  { name: 'Wrench', icon: Wrench, label: 'Ferramentas/Utilitários' },
  { name: 'Lightbulb', icon: Lightbulb, label: 'Inovação/Ideias' },
  { name: 'Star', icon: Star, label: 'Premium/Destaque' },
  { name: 'Award', icon: Award, label: 'Certificações' },
  { name: 'Target', icon: Target, label: 'Objetivos/Metas' },
  { name: 'BarChart3', icon: BarChart3, label: 'Estatísticas' },
  { name: 'PieChart', icon: PieChart, label: 'Relatórios' },
  { name: 'LineChart', icon: LineChart, label: 'Analytics' },
  { name: 'Activity', icon: Activity, label: 'Performance' },
  { name: 'Zap', icon: Zap, label: 'Energia/Rápido' },
  { name: 'Shield', icon: Shield, label: 'Segurança' },
  { name: 'Lock', icon: Lock, label: 'Privacidade' },
  { name: 'Key', icon: Key, label: 'Acesso/Chaves' }
]

const IconSelector = ({ selectedIcon = 'BookOpen', onIconSelect, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Filtrar ícones baseado no termo de busca
  const filteredIcons = availableIcons.filter(iconData =>
    iconData.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    iconData.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Encontrar o ícone selecionado atual
  const selectedIconData = availableIcons.find(icon => icon.name === selectedIcon) || availableIcons[0]
  const SelectedIconComponent = selectedIconData.icon

  const handleIconSelect = (iconName) => {
    onIconSelect(iconName)
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <div className={`relative ${className}`}>
      {/* Botão para abrir o seletor */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 w-full px-4 py-3 bg-slate-800/50 border border-slate-600/50 rounded-lg hover:bg-slate-700/50 hover:border-slate-500/50 transition-all duration-200 text-white"
      >
        <SelectedIconComponent className="w-5 h-5 text-red-400" />
        <span className="flex-1 text-left">{selectedIconData.label}</span>
        <div className="text-slate-400 text-sm">
          {isOpen ? '▲' : '▼'}
        </div>
      </button>

      {/* Dropdown com ícones */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-slate-800/95 backdrop-blur-xl border border-slate-600/50 rounded-lg shadow-2xl max-h-96 overflow-hidden">
          {/* Campo de busca */}
          <div className="p-3 border-b border-slate-700/50">
            <input
              type="text"
              placeholder="Buscar ícone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50"
            />
          </div>

          {/* Lista de ícones */}
          <div className="max-h-80 overflow-y-auto">
            <div className="grid grid-cols-1 gap-1 p-2">
              {filteredIcons.map((iconData) => {
                const IconComponent = iconData.icon
                const isSelected = iconData.name === selectedIcon

                return (
                  <button
                    key={iconData.name}
                    type="button"
                    onClick={() => handleIconSelect(iconData.name)}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-md transition-all duration-200 hover:bg-slate-700/50 ${
                      isSelected 
                        ? 'bg-red-600/20 border border-red-500/50 text-red-300' 
                        : 'text-slate-200 hover:text-white'
                    }`}
                  >
                    <IconComponent className={`w-4 h-4 ${isSelected ? 'text-red-400' : 'text-slate-400'}`} />
                    <span className="text-sm">{iconData.label}</span>
                    {isSelected && (
                      <div className="ml-auto w-2 h-2 bg-red-500 rounded-full"></div>
                    )}
                  </button>
                )
              })}
            </div>

            {filteredIcons.length === 0 && (
              <div className="p-6 text-center text-slate-400">
                <div className="text-sm">Nenhum ícone encontrado</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overlay para fechar quando clicar fora */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default IconSelector 